import Vue from 'vue'
import { mapActions } from 'vuex'
import router from './mainRouter'
import store from './mainStore'
import components from './components'
import filtering from './filtering'
import tracking from './tracking'
import translations from './translations'
import swiper from './swiper'
import './js/search/addSearch'
import 'lazysizes'
export const eventBus = new Vue()

import * as Sentry from '@sentry/browser'
import { captureConsoleIntegration } from '@sentry/integrations'

import Vuelidate from 'vuelidate'
Vue.use(Vuelidate)

import VueMeta from 'vue-meta'
Vue.use(VueMeta)

import VueScrollTo from 'vue-scrollto'
Vue.use(VueScrollTo)

import { VueReCaptcha } from 'vue-recaptcha-v3'
import './mainMixins'
import axios from 'axios'
import mainMixins from './mainMixins'

const envVars = document.querySelector('#root').dataset
Vue.use(VueReCaptcha, { siteKey: envVars.googleRecaptchaV3SiteKey })

Vue.config.productionTip = false
axios.defaults.baseURL = envVars.apiUrl

window.APP = new Vue({
    el: '#root',
    name: 'BreezelineApp',
    router,
    store,
    components,
    data() {
        return {
            autoAddressInState: false,
        }
    },
    beforeMount() {
        this.$store.dispatch('setEnvVariables', envVars)
        this.$store.dispatch('autoZip')
        this.$store.dispatch(
            'setAbandonedCartGiftCardTrigger',
            envVars.abandonedCartGiftCardTrigger ?? null
        )

        const queryString = new URLSearchParams(window.location.search)
        const referralCode = queryString.get('referral_code') ?? null
        let csgEnv = this.$store.state.csgEnvironment

        if (referralCode) {
            this.$store.dispatch('setReferralCode', referralCode)
        }
        if (csgEnv && envVars.environment !== 'production') {
            console.log('csgEnv: ' + csgEnv)
            axios.defaults.headers.common['X-Set-CSG-Environment'] = csgEnv
        }
        if (
            this.$route.path.includes('/online-account-manager') &&
            !this.$route.path.includes('/business')
        ) {
            const redirect = queryString.get('r') ?? true
            if (redirect === true) {
                window.location = '/interstitial-myaccount'
            }
        }

        if (queryString.has('check-address')) {
            this.$store.dispatch('toggleCheckAvailabilityModal', {
                visible: true,
                currentCustomerCheck: false,
                movingToAddress: true,
            })
        }

        const autoAddressKey = queryString.get('autoAddressKey') ?? null
        if (autoAddressKey) {
            const line1 = queryString.get('acstreetaddress') ?? null
            const line2 = queryString.get('acaptUnitnum') ?? null
            const city = queryString.get('accity') ?? null
            const state = queryString.get('acstate') ?? null
            const postalCode = queryString.get('aczip') ?? null

            if (line1 && city && state && postalCode && autoAddressKey) {
                this.autoAddressInState = 'loading'
                this.checkAddress(
                    line1,
                    line2,
                    city,
                    state,
                    postalCode,
                    autoAddressKey
                )
            }
        }
    },
    mounted() {
        this.$store.dispatch('Personalization/initialize', {
            url: this.$store.state.env.personalizationEventLoggerUrl,
        })
        
        // Get Current Promotions
        this.$store.dispatch('Promotions/initialize')
            .then(() => {
                this.$store.dispatch('Promotions/checkForPromotions');
            })

        // if there is a zip in the Personalization cookie, re-address the user (but do not reload the page!)
        // only do this if there is no location id yet in the main cookie
        if (
            this.$store.state.Personalization.user.attributes.zip &&
            !this.$store.state.meta.locationId &&
            !this.$store.state.forcedMarket?.marketType
        ) {
            this.$store.dispatch(
                'checkZip',
                this.$store.state.Personalization.user.attributes.zip
            )
        }

        this.initializeQueryParameters()

        setTimeout(() => {
            const hash = window.location.hash

            if (hash) {
                this.$scrollTo(hash)
            }
        }, 1000)
    },
    mixins: [filtering, tracking, translations, swiper],
    computed: {
        kickedOut() {
            return this.$store.state.Buyflow.session.showNotification
        },
        forcedMarketChange() {
            return this.$store.state.forcedMarket.showPopup
        },
        ineligibleForOffer() {
            return window.location.search.indexOf('ineligible') > -1
        },
    },
    methods: {
        ...mapActions('Promotions', ['setGiftChoicePopup']),
        checkAddress(line1, line2, city, state, postalCode, autoAddressKey) {
            return this.$recaptchaLoaded()
                .then(() => {
                    this.$recaptcha('on_submit')
                        .then((token) => {
                            this.$store
                                .dispatch('checkAddress', {
                                    line1: line1,
                                    line2: line2 ?? '',
                                    city: city,
                                    stateOrProvince: state,
                                    postalCode: postalCode,
                                    emailAddress: '',
                                    recaptchaToken: token,
                                    autoAddressKey: autoAddressKey,
                                })
                                .then((response) => {
                                    if (response.isValid) {
                                        mainMixins.sendEvent(
                                            '',
                                            'User comes from AllConnect and is serviceable',
                                            '',
                                            'all_connect_serviceable_lead'
                                        )

                                        this.autoAddressInState = 'success'
                                    } else {
                                        mainMixins.sendEvent(
                                            '',
                                            "User comes from AllConnect and isn't serviceable",
                                            '',
                                            'all_connect_failed_address'
                                        )

                                        this.autoAddressInState = 'failed'
                                    }
                                })
                                .catch(() => {
                                    mainMixins.sendEvent(
                                        '',
                                        "User comes from AllConnect and isn't serviceable",
                                        '',
                                        'all_connect_failed_address'
                                    )

                                    this.autoAddressInState = 'failed'
                                })
                                .finally(() => {
                                    this.clearAutoAddress()
                                })
                        })
                        .catch(() => {
                            mainMixins.sendEvent(
                                '',
                                "User comes from AllConnect and isn't serviceable",
                                '',
                                'all_connect_failed_address'
                            )
                            this.clearAutoAddress('failed')
                        })
                })
                .catch(() => {
                    mainMixins.sendEvent(
                        '',
                        "User comes from AllConnect and isn't serviceable",
                        '',
                        'all_connect_failed_address'
                    )
                    this.clearAutoAddress('failed')
                })
        },
        clearAutoAddress(
            currentValue = this.autoAddressInState,
            timeout = 1500
        ) {
            this.autoAddressInState = currentValue
            setTimeout(() => {
                if (currentValue === 'success') {
                    window.location.href = window.location.pathname + '#offers'
                }
                this.autoAddressInState = false
            }, timeout)
        },
        kickedOutAcknowledged() {
            this.$store.dispatch('Buyflow/clearExpiredSessionNotification')
        },
        forcedMarketAcknowledged() {
            this.$store.dispatch('setForcedMarket', {
                marketType: null,
                showPopup: false,
            })
        },
        ineligibleForOfferAcknowledged() {
            window.location = window.location.href.split('?')[0]
        },
        /**
         * Check for specific campaign id's from InCloud and convert the hero's
         * primary button that sends the user into the buyflow with a preselected
         * offer. It will also fire any GA events given.
         */
        updateHero(event) {
            axios
                .get(`${this.$store.state.env.apiUrl}/api/features/hgct`)
                // check to see if the hgct (Hero Gift Card Trigger) feature
                // is available. If we get a 404, we know it's not enabled.
                .then((response) => {
                    const campaigns = response.data.data.value?.campaigns

                    const matchingCampaign = campaigns.find(
                        (campaign) => campaign.campaignId === event.campaign.campaignId
                    )

                    if (matchingCampaign) {
                        const element = document.getElementById(
                            event.element.id
                        )

                        this.$store.commit('SET_GIFT_CARD_ELIGIBLE', matchingCampaign)

                        const button = element.querySelector('a')

                        button.addEventListener('click', (event) => {
                            event.preventDefault()

                            if (
                                this.$store.state.residentialOffers.length === 0
                            ) {
                                this.$store.dispatch(
                                    'toggleCheckAvailabilityModal',
                                    {
                                        visible: true,
                                        routeToBuyflow: true,
                                        resetSelectedOffer: false,
                                        hasSelectedGiftCard: true,
                                        currentCustomerCheck:
                                            this.$store.state.meta
                                                .isCurrentCustomer || false,
                                    }
                                )

                                return
                            }

                            this.$store.dispatch('handleGiftCardEligibility')
                        })
                    }
                })
                .catch((error) => {
                    console.log(error)
                })
        },
        initializeQueryParameters() {
            // Use query parameters function to set store values
            const queryString = window.location.search
            const queryParams = this.extractQueryParameters(queryString)

            // CLEARLINK
            if (queryParams.clearlinkId && queryParams.clearlinkOfferId) {
                this.$store.dispatch('setClearlinkId', queryParams.clearlinkId)
            }

            // ALLCONNECT
            if (
                queryParams.acaid === 'ALLCONNECT' &&
                queryParams.acaid !== null
            ) {
                this.$store.dispatch('setAcsid', queryParams.acsid)
            }

            // IMPACT RADIUS
            if (queryParams.irclickid) {
                console.log('Impact Radius Click ID:', queryParams.irclickid)
                this.$store.dispatch('setImpactRadiusClickId', queryParams.irclickid)
            }
        },
        extractQueryParameters(queryString) {
            const params = new URLSearchParams(queryString)

            // Grab and store query parameters
            return {
                clearlinkId: params.get('CXL'),
                clearlinkOfferId: params.get('offer_id'),
                acsid: params.get('acsid'),
                acaid: params.get('acaid'),
                irclickid: params.get('irclickid'),
            }
        },
    },
})

// - GA page view reporting block -
window.dataLayer = window.dataLayer || []

function gtag() {
    window.dataLayer.push(arguments)
}

if (router.currentRoute.name) {
    gtag('config', store.state.env.googleAnalytics, {
        page_path: router.currentRoute.path + window.location.search,
        page_location: String(window.location + window.location.search),
        app_name: 'Breezeline',
        send_page_view: true,
    })

    router.beforeEach((to, from, next) => {
        const hasMultipleChoicePromo = store.getters['Promotions/hasMultipleChoicePromo'];
        const multipleChoicePromo = store.getters['Promotions/multipleChoicePromo'];
        const selectedGiftOption = store.state.Promotions.selectedGiftOption;
        
        if (hasMultipleChoicePromo && !selectedGiftOption && multipleChoicePromo) {
            // Show popup
            store.dispatch('Promotions/setGiftChoicePopup', true);
        } else {
            next();
        }
    })

    router.afterEach((to) => {
        gtag('config', store.state.env.googleAnalytics, {
            page_path: to.path + window.location.search,
            page_location: String(window.location + window.location.search),
            app_name: 'Breezeline',
            send_page_view: true,
        })
    })
} else {
    gtag('config', store.state.env.googleAnalytics, {
        page_path: String(window.location.pathname + window.location.search),
        page_location: String(window.location + window.location.search),
        app_name: 'Breezeline',
        send_page_view: true,
    })
}
// - GA page view reporting block end -
Sentry.init({
    debug: false,
    dsn: 'https://cdf9b241d3c64f07a41df70564179e4e@o4506837683011584.ingest.us.sentry.io/5598862' /* eslint-disable-line */,
    release: process.env.COMMIT_HASH,
    environment: envVars.environment,
    ignoreErrors: [
        'mpel/mpel_ssd',
        'pagead/viewthroughconversion',
        // https://github.com/getsentry/sentry-javascript/issues/2514
        'Non-Error promise rejection captured with value: Timeout',

        // Internal Sentry issue, we can ignore
        'r._handler is not a function',

        // https://stackoverflow.com/a/50387233/150367
        'ResizeObserver loop limit exceeded',

        // qualtrics always throws noisy messages
        'siteintercept.qualtrics.com',

        "Can't find variable: _AutofillCallbackHandler"
    ],
    allowUrls: [
        /https?:\/\/([\w-]+\.)*breezeline\.com/,
        /https?:\/\/([\w-]+\.)*ddev\.site/,
        /https?:\/\/([\w-]+\.)*imarc\.io/,
    ],
    denyUrls: [
        /secure\.quantserve\.com/i,
        /apps\.mypurecloud\.com/i,
        /doubleclick\.net/i,
    ],
    // the percentage of users that will have performance data monitored
    tracesSampleRate: 0.01,

    // the percent of users that will have fully recorded sessions
    replaysSessionSampleRate: 0.01,

    // the percent of users that will have recorded sessions when errors occur, this includes a 1-minute buffer before the error starts
    replaysOnErrorSampleRate: 0.01,

    integrations: [
        new Sentry.Replay({
            maskAllInputs: true,
            blockAllMedia: false,
            maskAllText: false,
        }),
        captureConsoleIntegration({
            // Please do not enable warn or log level events in production unless
            // you are actively debugging an issue, otherwise this will blow up Sentry $$$.
            levels: ['error'],
        }),
    ],
})

// Removes Data attributes from the Root Div
const elem = document.querySelector('#root')
const dataset = elem.dataset
for (let key in dataset) {
    elem.removeAttribute(
        'data-' +
            key
                .split(/(?=[A-Z])/)
                .join('-')
                .toLowerCase()
    )
}

const searchCalloutBtn = document.querySelector('#searchCalloutBtn')
if (document.URL.includes('/business/support/')) {
    searchCalloutBtn?.setAttribute('href', '/business/contact-us-business')
} else {
    searchCalloutBtn?.setAttribute('href', '/contact-us')
}
